import { useServerRequest } from '../api'
import { videoNextKey } from '../keys'
import { useVideoCache } from './cache'
import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
  type InfiniteData,
  type QueryFunction,
  type QueryKey,
} from '@tanstack/react-query'
import {
  Logger,
  HOUR,
  type APIError,
  messageFromError,
  type VideoNextInput,
  type VideoNextSuccess,
} from '@terros/common'

type Result = Omit<UseInfiniteQueryResult<InfiniteData<VideoNextSuccess>>, 'error'> & {
  error?: string
}

const logger = new Logger('useNextVideos')

export function useNextVideos(enabled = true): Result {
  const serverRequest = useServerRequest<VideoNextInput, VideoNextSuccess>('/video/next')
  const cache = useVideoCache()
  const queryFn: QueryFunction<VideoNextSuccess, QueryKey, string | undefined> = async ({
    pageParam,
  }): Promise<VideoNextSuccess> => {
    const res = await serverRequest({
      recommId: pageParam,
    })
    logger.info(`Got next page of ${res.videos.length} videos`)
    cache.addItems(res.videos)
    return res
  }
  const query = useInfiniteQuery<
    VideoNextSuccess,
    APIError,
    InfiniteData<VideoNextSuccess>,
    QueryKey,
    string | undefined
  >({
    queryKey: videoNextKey(),
    queryFn,
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.recommId,
    enabled,
    staleTime: 6 * HOUR,
    gcTime: 6 * HOUR,
  })
  const error = messageFromError(query.error)
  return { ...query, error }
}
